import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { FAQS } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, CtaBand, usePageMeta } from "@/components/shared";

function FaqItem({ q, a, open, onToggle, index }) {
  return (
    <div className="border-t border-navy/10 last:border-b">
      <button
        onClick={onToggle}
        data-testid={`faq-question-${index}`}
        className="flex w-full items-center justify-between gap-6 py-6 text-left transition-colors hover:text-bermuda-dark"
      >
        <span className="font-serif text-xl text-navy sm:text-2xl">{q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="shrink-0 text-bermuda-dark">
          <Plus size={20} strokeWidth={1.5} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p data-testid={`faq-answer-${index}`} className="max-w-3xl pb-7 text-sm leading-relaxed text-navy/65">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Faq() {
  usePageMeta("FAQ", "Answers for Bermuda vacation rental owners — co-hosting, cleaning, maintenance, pricing, inspections and overseas ownership.");
  const [open, setOpen] = useState(0);
  return (
    <main>
      <section className="mx-auto max-w-[1100px] px-6 pb-24 pt-40 lg:px-10">
        <SectionHead
          overline="Questions, Answered"
          title={<>Everything owners<br /><span className="italic text-bermuda-dark">ask us first.</span></>}
          copy="Straight answers about co-hosting, management, cleaning, maintenance and working together."
        />
        <Reveal delay={0.15}>
          <div className="mt-14" data-testid="faq-accordion">
            {FAQS.map(([q, a], i) => (
              <FaqItem key={q} q={q} a={a} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
            ))}
          </div>
        </Reveal>
      </section>
      <CtaBand title="Still have a question about your property?" copy="Ask us directly — every consultation begins with your questions, not our pitch." />
    </main>
  );
}
